import { supabase } from "@/integrations/supabase/client";

export type UserRole = "admin" | "auctioneer" | "clerk" | "bidder";
export type ApprovalStatus = "pending" | "approved" | "rejected";

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (
  userRoles: UserRole[],
  requiredRoles: UserRole[]
): boolean => {
  return requiredRoles.some(role => userRoles.includes(role));
};

/**
 * Check if a user has all of the specified roles
 */
export const hasAllRoles = (
  userRoles: UserRole[],
  requiredRoles: UserRole[]
): boolean => {
  return requiredRoles.every(role => userRoles.includes(role));
};

/**
 * Check if user is staff (admin, auctioneer, or clerk)
 */
export const isStaffRole = (userRoles: UserRole[]): boolean => {
  return hasAnyRole(userRoles, ["admin", "auctioneer", "clerk"]);
};

/**
 * Fetch user roles from database
 */
export const getUserRoles = async (userId: string): Promise<UserRole[]> => {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);

  if (error) {
    console.error("Error fetching user roles:", error);
    return [];
  }

  return data?.map(r => r.role as UserRole) || [];
};

/**
 * Fetch user approval status from profile
 */
export const getApprovalStatus = async (
  userId: string
): Promise<ApprovalStatus | null> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("approval_status")
    .eq("id", userId)
    .single();

  if (error) {
    console.error("Error fetching approval status:", error);
    return null;
  }

  return (data?.approval_status as ApprovalStatus) || null;
};

/**
 * Verify that a user's profile exists
 * Useful after signup to ensure database trigger created the profile
 */
export const verifyProfileExists = async (
  userId: string
): Promise<boolean> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("id")
    .eq("id", userId)
    .single();

  if (error) {
    console.error("Profile verification error:", error);
    return false;
  }

  return !!data;
};

/**
 * Get user profile with all details
 */
export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }

  return data;
};

/**
 * Determine redirect path based on user role and approval status
 */
export const getRedirectPath = (
  roles: UserRole[],
  approvalStatus: ApprovalStatus | null
): string => {
  const isStaff = isStaffRole(roles);
  const isPending = approvalStatus === "pending";
  const isApproved = approvalStatus === "approved";

  // Staff always goes to dashboard
  if (isStaff) {
    return "/dashboard";
  }

  // Pending or non-approved users go to home
  if (isPending || !isApproved) {
    return "/";
  }

  // Approved bidders can go to streams
  if (isApproved && roles.includes("bidder")) {
    return "/streams";
  }

  // Default fallback
  return "/";
};

/**
 * Enhanced error message handler for auth errors
 */
export const getAuthErrorMessage = (error: unknown): string => {
  const explicit = (error as { message?: string } | null)?.message;
  let message: string;
  if (typeof explicit === "string" && explicit.length > 0) {
    message = explicit;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object") {
    try {
      message = JSON.stringify(error);
    } catch {
      message = String(error);
    }
  } else {
    message = String(error);
  }

  // Map common Supabase auth errors to user-friendly messages
  if (message.includes("Email not confirmed")) {
    return "Please verify your email before signing in. Check your inbox for the verification link.";
  }

  if (message.includes("Invalid login credentials")) {
    return "Invalid email or password. Please try again.";
  }

  if (message.includes("Email rate limit exceeded")) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }

  if (message.includes("User already registered")) {
    return "An account with this email already exists. Please sign in instead.";
  }

  if (message.includes("Password should be at least")) {
    return "Password must be at least 6 characters long.";
  }

  if (message.includes("Unable to validate email address")) {
    return "Invalid email address format.";
  }

  if (message.includes("Network request failed")) {
    return "Network error. Please check your connection and try again.";
  }

  // Return original message if no specific mapping found
  return message;
};
